import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'

import {
  isCsrfRejection,
  readXsrfToken,
  reportXsrfRecovery,
  storeXsrfToken,
  XSRF_REQUEST_HEADER_NAME,
  XSRF_RESPONSE_HEADER_NAME
} from './xsrf'

let numberOfAjaxCallPending = 0
let loadStarted: Date = null
let timer: NodeJS.Timeout = null

type RetriedRequestConfig = InternalAxiosRequestConfig & { xsrfRetried?: boolean }

const responseXsrfToken = (headers: AxiosError['response']['headers']): string | null => {
  const token = headers?.[XSRF_RESPONSE_HEADER_NAME.toLocaleLowerCase()]

  return typeof token === 'string' && token ? token : null
}

type Type = {
  ejectInterceptors: () => void
}
const setAxiosInterceptor = (): Type => {
  const xsrfRequestId = axios.interceptors.request.use(async (request) => {
    const value = await readXsrfToken()
    if (value) {
      request.headers[XSRF_REQUEST_HEADER_NAME] = value
    }

    return request
  })

  // Add a response interceptor
  const xsrfResponseId = axios.interceptors.response.use(
    async (response) => {
      const xsrfToken = responseXsrfToken(response.headers)

      if (xsrfToken) {
        await storeXsrfToken(xsrfToken)
      }

      return response
    },
    async (error: AxiosError) => {
      const config = error.config as RetriedRequestConfig
      const xsrfToken = responseXsrfToken(error.response?.headers)
      const healable =
        isCsrfRejection(error.response?.status, error.response?.data) &&
        xsrfToken &&
        config &&
        !config.xsrfRetried

      if (!healable) return Promise.reject(error)

      // One retry only, so a session the token cannot fix still surfaces its own error.
      config.xsrfRetried = true
      await storeXsrfToken(xsrfToken)
      reportXsrfRecovery(config.url || 'REST')

      return axios(config)
    }
  )

  const loadTimeRequestId = axios.interceptors.request.use(
    (config) => {
      if (!loadStarted) loadStarted = new Date()
      numberOfAjaxCallPending++
      return config
    },
    function (error) {
      return Promise.reject(error)
    }
  )

  // Add a response interceptor
  const loadTimeResponseId = axios.interceptors.response.use(
    (response) => {
      numberOfAjaxCallPending--
      clearTimeout(timer)
      if (numberOfAjaxCallPending === 0) {
        timer = setTimeout(() => {
          const diff =
            new Date().getTime() - 3000 - (loadStarted?.getTime?.() || new Date().getTime())
          const loadTime = Math.abs(Math.round(diff))
          window.heap?.track('Page Load', { 'Load Time': loadTime })
          loadStarted = null
        }, 3000)
      }
      return response
    },
    (error) => {
      numberOfAjaxCallPending--
      if (numberOfAjaxCallPending === 0) {
        //hide loader
      }
      return Promise.reject(error)
    }
  )

  const ejectInterceptors = () => {
    axios.interceptors.request.eject(xsrfRequestId)
    axios.interceptors.request.eject(loadTimeRequestId)

    axios.interceptors.response.eject(xsrfResponseId)
    axios.interceptors.response.eject(loadTimeResponseId)
  }

  return { ejectInterceptors }
}

export default setAxiosInterceptor
